import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  savedContentTypes,
  savedItems,
  savedDashboardStats,
  savedUserProfile,
  getTopSavedItems,
  getFavorites,
  getPendingItems,
  getContentTypeById,
  type SavedItem,
  type SavedContentTypeId,
} from '@/mocks/rfwSavedItems';

function SavedCard({ item, size = 'md' }: { item: SavedItem; size?: 'sm' | 'md' }) {
  const typeInfo = getContentTypeById(item.type);
  const isCompact = size === 'sm';
  return (
    <div className={`rounded-2xl bg-background-50 border border-background-200/70 overflow-hidden hover:border-foreground-300/50 transition-all duration-200 group ${isCompact ? '' : 'flex flex-col'}`}>
      <div className={`relative overflow-hidden ${isCompact ? 'h-24' : 'h-44'}`}>
        <img src={item.thumbnailUrl} alt={item.title} className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
        <div className="absolute top-3 left-3">
          {typeInfo && (
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold bg-white/20 backdrop-blur-sm text-white`}>
              <i className={`${typeInfo.icon} text-[9px]`}></i>
              {typeInfo.name}
            </span>
          )}
        </div>
        {item.isFavorite && (
          <div className="absolute top-3 right-3">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500 text-white text-[9px] font-bold">
              <i className="ri-star-fill text-[8px]"></i>
            </span>
          </div>
        )}
        {item.isPending && (
          <div className="absolute bottom-3 right-3">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-500/80 text-white text-[9px] font-semibold">
              <i className="ri-time-line text-[8px]"></i>Pendiente
            </span>
          </div>
        )}
      </div>
      <div className={`${isCompact ? 'p-3' : 'p-4 flex-1 flex flex-col'}`}>
        <p className={`font-semibold text-foreground-950 line-clamp-2 leading-snug mb-2 ${isCompact ? 'text-xs' : 'text-sm'}`}>{item.title}</p>
        {!isCompact && item.subtitle && (
          <p className="text-[10px] text-foreground-500 mb-2">{item.subtitle}</p>
        )}
        <div className="flex items-center gap-2 mt-auto">
          <img src={item.authorPhoto} alt={item.authorName} className="w-5 h-5 rounded-full object-cover object-top" />
          <span className="text-[10px] text-foreground-500 font-medium truncate">{item.radioName}</span>
          <span className="text-[9px] text-foreground-400 ml-auto whitespace-nowrap">{item.savedAt}</span>
        </div>
      </div>
    </div>
  );
}

export default function GuardadosSection() {
  const [activeTab, setActiveTab] = useState<'destacados' | 'favoritos' | 'pendientes'>('destacados');
  const topItems = getTopSavedItems(6);
  const favorites = getFavorites(9);
  const pending = getPendingItems(6);

  const tabs = [
    { id: 'destacados' as const, label: 'Destacados', icon: 'ri-bookmark-line' },
    { id: 'favoritos' as const, label: 'Favoritos', icon: 'ri-star-line' },
    { id: 'pendientes' as const, label: 'Pendientes', icon: 'ri-time-line' },
  ];

  return (
    <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-teal-100 text-teal-700 text-xs font-semibold mb-4">
            <span className="w-2 h-2 rounded-full bg-teal-500 animate-pulse"></span>
            SISTEMA DE GUARDADOS
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
            Guardar Debe Ser Más Fácil Que <span className="text-teal-600">Buscar Después</span>
          </h2>
          <p className="text-foreground-600 max-w-2xl mx-auto text-sm md:text-base">
            Posts, videos, eventos, concursos, programas, locutores y radios. Todo lo que importa, a un toque de distancia en Mis Guardados.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-10">
          {[
            { label: 'Guardados Totales', value: (savedDashboardStats.totalSaved / 1000).toFixed(1) + 'K', icon: 'ri-bookmark-line', color: 'text-teal-600 bg-teal-50' },
            { label: 'Hoy', value: savedDashboardStats.totalSavedToday.toLocaleString(), icon: 'ri-calendar-line', color: 'text-sky-600 bg-sky-50' },
            { label: 'Más Guardado', value: savedDashboardStats.mostSavedType, sub: savedDashboardStats.mostSavedTypeCount.toLocaleString(), icon: 'ri-radio-line', color: 'text-amber-600 bg-amber-50' },
            { label: 'Favoritos', value: (savedDashboardStats.totalFavorites / 1000).toFixed(1) + 'K', icon: 'ri-star-line', color: 'text-orange-600 bg-orange-50' },
            { label: 'Pendientes', value: (savedDashboardStats.totalPending / 1000).toFixed(1) + 'K', icon: 'ri-time-line', color: 'text-violet-600 bg-violet-50' },
          ].map((stat, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-background-50 border border-background-200/70">
              <div className={`w-9 h-9 rounded-full ${stat.color} flex items-center justify-center shrink-0`}>
                <i className={`${stat.icon} text-sm`}></i>
              </div>
              <div>
                <p className="text-lg font-bold text-foreground-950">{stat.value}</p>
                <p className="text-[10px] text-foreground-500">{stat.label}</p>
                {stat.sub && <p className="text-[9px] text-foreground-400">{stat.sub}</p>}
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-center mb-8">
          <div className="inline-flex bg-background-100 rounded-full p-1 border border-background-200/70">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeTab === tab.id
                    ? 'bg-teal-500 text-white shadow-sm'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                <i className={`${tab.icon} text-[10px]`}></i>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {activeTab === 'destacados' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {topItems.map((item) => (
                <SavedCard key={item.id} item={item} />
              ))}
            </div>

            <div>
              <h4 className="text-sm font-bold text-foreground-950 mb-3">¿Qué Podés Guardar?</h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
                {savedContentTypes.map((ct) => (
                  <div key={ct.id} className={`p-3 rounded-xl ${ct.bgColor} border border-background-200/70 text-center`}>
                    <div className="w-9 h-9 rounded-full bg-white/70 flex items-center justify-center mx-auto mb-2">
                      <i className={`${ct.icon} ${ct.color} text-base`}></i>
                    </div>
                    <p className={`text-xs font-bold ${ct.textColor}`}>{ct.name}</p>
                    <p className="text-[9px] text-foreground-500 mt-0.5">{ct.totalSaved.toLocaleString()}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'favoritos' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {favorites.map((item) => (
              <SavedCard key={item.id} item={item} />
            ))}
          </div>
        )}

        {activeTab === 'pendientes' && (
          <div className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {pending.map((item) => (
                <SavedCard key={item.id} item={item} />
              ))}
            </div>
            {pending.length === 0 && (
              <div className="text-center py-10">
                <div className="w-14 h-14 rounded-full bg-teal-50 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-check-double-line text-2xl text-teal-500"></i>
                </div>
                <p className="text-sm font-semibold text-foreground-950 mb-1">¡Todo al día!</p>
                <p className="text-xs text-foreground-500">No tenés contenido pendiente por ver.</p>
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-10">
          <div className="md:col-span-2 p-5 rounded-2xl bg-teal-50 border border-teal-200/50">
            <h4 className="text-sm font-bold text-foreground-950 mb-3">Acceso Rápido desde tu Perfil</h4>
            <div className="flex items-center gap-4">
              <img src={savedUserProfile.photo} alt={savedUserProfile.name} className="w-12 h-12 rounded-full object-cover object-top ring-2 ring-teal-200" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-foreground-950">{savedUserProfile.name}</p>
                <div className="flex items-center gap-3 mt-1 text-[10px] text-foreground-500">
                  <span><i className="ri-bookmark-line mr-0.5"></i>{savedUserProfile.totalSaved} guardados</span>
                  <span><i className="ri-star-line mr-0.5"></i>{savedUserProfile.totalFavorites} favs</span>
                  <span><i className="ri-time-line mr-0.5"></i>{savedUserProfile.totalPending} pendientes</span>
                </div>
              </div>
              <Link to="/guardados" className="inline-flex items-center gap-1 px-4 py-2 rounded-full bg-teal-500 hover:bg-teal-600 text-white text-xs font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer">
                <i className="ri-bookmark-line text-[10px]"></i>
                Mis Guardados
              </Link>
            </div>
          </div>
          <div className="p-5 rounded-2xl bg-background-100 border border-background-200/70">
            <h4 className="text-sm font-bold text-foreground-950 mb-3">Filtros Rápidos</h4>
            <div className="space-y-2">
              {[
                { id: 'recientes', label: 'Recientes', icon: 'ri-time-line', desc: 'Últimos guardados primero' },
                { id: 'mas-vistos', label: 'Más Vistos', icon: 'ri-eye-line', desc: 'Por popularidad' },
                { id: 'favoritos', label: 'Favoritos', icon: 'ri-star-line', desc: 'Los que marcaste ★' },
                { id: 'pendientes', label: 'Pendientes', icon: 'ri-hourglass-line', desc: 'Lo que falta ver' },
              ].map((filter) => (
                <div key={filter.id} className="flex items-center gap-3 p-2.5 rounded-xl bg-background-50 border border-background-200/70">
                  <div className="w-8 h-8 rounded-full bg-teal-50 flex items-center justify-center shrink-0">
                    <i className={`${filter.icon} text-teal-600 text-sm`}></i>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-foreground-950">{filter.label}</p>
                    <p className="text-[9px] text-foreground-500">{filter.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="text-center mt-10">
          <Link
            to="/guardados"
            className="inline-flex items-center gap-2 px-6 py-3 bg-teal-500 hover:bg-teal-600 text-white font-semibold rounded-full transition-all duration-200 text-sm whitespace-nowrap"
          >
            <i className="ri-bookmark-line"></i>
            Ir a Mis Guardados
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        <div className="mt-10 p-6 rounded-2xl bg-teal-100/70 border border-teal-200/50 text-center">
          <div className="w-10 h-10 rounded-full bg-teal-500 flex items-center justify-center mx-auto mb-3">
            <i className="ri-bookmark-line text-lg text-white"></i>
          </div>
          <p className="text-sm font-bold text-foreground-950 mb-1">
            Guardar debe ser más fácil que buscar después.
          </p>
          <p className="text-xs text-foreground-600 max-w-lg mx-auto">
            Un solo toque en el botón de guardar y el contenido queda en tu perfil para siempre. Sin menús, sin pasos extra, sin tener que recordar dónde estaba. Cuando lo necesites, lo encontrás en Mis Guardados con los filtros que necesitás.
          </p>
        </div>
      </div>
    </section>
  );
}